import toast from 'react-hot-toast'

/**
 * Enhanced error handler for authentication and API errors
 * @param {Error} error - The error object from the API
 * @param {string} defaultMessage - Default message if no specific error handling is found
 */
export const handleAuthError = (error, defaultMessage = 'An error occurred') => {
  console.log('[ERROR HANDLER] Received error:', error)
  console.log('[ERROR HANDLER] Error response data:', error.response?.data)
  console.log('[ERROR HANDLER] Error code:', error.response?.data?.code)
  console.log('[ERROR HANDLER] Error message:', error.response?.data?.message)
  
  const errorData = error.response?.data || {}
  const message = errorData.message || defaultMessage
  const code = errorData.code
  const details = errorData.details

  // Handle validation errors with field-specific messages
  if (details && Array.isArray(details)) {
    details.forEach(detail => {
      const fieldMessage = `${detail.field}: ${detail.message}`
      toast.error(fieldMessage)
    })
    return
  }

  // Handle specific error codes
  switch (code) {
    // Authentication errors
    case 'MISSING_CREDENTIALS':
      toast.error('Please enter both email and password')
      break
    case 'INVALID_EMAIL_FORMAT':
      toast.error('Please enter a valid email address')
      break
    case 'INVALID_PASSWORD_LENGTH':
      toast.error('Password must be at least 6 characters long')
      break
    case 'INVALID_CREDENTIALS':
      toast.error('Email or password is incorrect')
      break
    case 'ACCOUNT_LOCKED':
      toast.error('Your account has been temporarily locked. Please try again later.')
      break

    // Registration errors
    case 'VALIDATION_ERROR':
      toast.error('Please correct the form errors and try again')
      break
    case 'EMAIL_EXISTS':
    case 'USER_EXISTS':
      toast.error('An account with this email already exists. Please use a different email or sign in.')
      break

    // Google OAuth errors
    case 'MISSING_GOOGLE_DATA':
      toast.error('Invalid Google authentication data. Please try again.')
      break
    case 'INVALID_GOOGLE_EMAIL':
      toast.error('Invalid email from Google. Please contact support.')
      break
    case 'ACCOUNT_CONFLICT':
      toast.error('Unable to link Google account. Please contact support.')
      break

    // System errors
    case 'DATABASE_ERROR':
      toast.error('Service temporarily unavailable. Please try again in a few minutes.')
      break
    case 'ENCRYPTION_ERROR':
      toast.error('Unable to secure your password. Please try again.')
      break
    case 'TOKEN_ERROR':
      toast.error('Authentication error. Please try again.')
      break
    case 'RATE_LIMITED':
      toast.error('Too many attempts. Please wait before trying again.')
      break
    case 'INTERNAL_ERROR':
      toast.error('Server error. Please try again later.')
      break

    // Password reset errors
    case 'RESET_EMAIL_SENT':
      toast.success('Password reset email sent successfully!')
      break
    case 'EMAIL_SERVICE_ERROR':
      toast.error('Unable to send reset email. Please try again later.')
      break
    case 'MISSING_RESET_DATA':
      toast.error('Invalid reset request. Please try again.')
      break
    case 'INVALID_RESET_TOKEN':
      toast.error('This reset link is invalid or has expired. Please request a new one.')
      break
    case 'PASSWORD_RESET_SUCCESS':
      toast.success('Password reset successfully! You can now log in.')
      break
    case 'MISSING_TOKEN':
      toast.error('Reset token is missing. Please use the link from your email.')
      break
    case 'VALID_TOKEN':
      // Don't show anything for valid token verification
      break

    default:
      // Handle network and HTTP errors
      if (!error.response) {
        toast.error('Network error. Please check your connection and try again.')
      } else if (error.response.status >= 500) {
        toast.error('Server error. Please try again later.')
      } else if (error.response.status === 429) {
        toast.error('Too many attempts. Please wait before trying again.')
      } else if (error.response.status === 404) {
        toast.error('Service not found. Please try again later.')
      } else if (error.response.status === 403) {
        toast.error('Access denied. Please check your permissions.')
      } else {
        toast.error(message)
      }
  }
}

/**
 * Validates form data on the client side before submission
 * @param {Object} data - Form data to validate
 * @param {string} formType - Type of form ('login' or 'register')
 * @returns {boolean} - True if validation passes, false otherwise
 */
export const validateFormData = (data, formType) => {
  if (formType === 'login') {
    if (!data.email?.trim()) {
      toast.error('Please enter your email address')
      return false
    }
    
    if (!data.password?.trim()) {
      toast.error('Please enter your password')
      return false
    }
    
    // Temporarily disabled for testing - remove this comment to re-enable
    // if (data.password.length < 6) {
    //   toast.error('Password must be at least 6 characters long')
    //   return false
    // }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(data.email.trim())) {
      toast.error('Please enter a valid email address')
      return false
    }
  }

  if (formType === 'register') {
    if (!data.name?.trim()) {
      toast.error('Please enter your full name')
      return false
    }
    
    if (data.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters long')
      return false
    }
    
    if (!data.email?.trim()) {
      toast.error('Please enter your email address')
      return false
    }
    
    // Temporarily disabled for testing - remove this comment to re-enable
    // Email format validation
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    // if (!emailRegex.test(data.email.trim())) {
    //   toast.error('Please enter a valid email address')
    //   return false
    // }
    
    if (!data.password?.trim()) {
      toast.error('Please enter a password')
      return false
    }
    
    if (data.password.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return false
    }
    
    if (data.phone && data.phone.trim().length > 0 && data.phone.trim().length < 8) {
      toast.error('Phone number must be at least 8 digits')
      return false
    }
  }

  return true
}

/**
 * Sanitizes form data before submission
 * @param {Object} data - Raw form data
 * @param {string} formType - Type of form ('login' or 'register')
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (data, formType) => {
  if (formType === 'login') {
    return {
      email: data.email?.trim().toLowerCase(),
      password: data.password
    }
  }

  if (formType === 'register') {
    return {
      name: data.name?.trim(),
      email: data.email?.trim().toLowerCase(),
      password: data.password,
      phone: data.phone?.trim() || null,
      address: data.address?.trim() || null
    }
  }

  return data
}
